import { useState } from 'react';
import { createStripeSession, type OrderSummary } from '../api/orders';
import { ApiError } from '../api/client';

/**
 * Stripe Checkout is the only way a buyer can pay, so a failed session must say
 * so out loud — there is no second channel to silently fall back to.
 */
export function StripePayButton({
  order,
  token,
  compact = false,
}: {
  order: OrderSummary;
  token: string;
  compact?: boolean;
}) {
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState<'outage' | 'other' | null>(null);

  const start = async () => {
    setLoading(true);
    try {
      const session = await createStripeSession(order.id, token);
      window.location.href = session.url;
    } catch (err) {
      // 409 means the order moved on without us — usually the webhook settled it
      // in another tab. Re-read it rather than claim an outage.
      if (err instanceof ApiError && err.status === 409) {
        window.location.reload();
        return;
      }
      console.error('[stripe] session failed', err);
      setFailure(err instanceof ApiError && err.status < 500 ? 'other' : 'outage');
    } finally {
      setLoading(false);
    }
  };

  if (failure) {
    return (
      <div
        style={{
          background: 'rgba(255,107,26,.08)',
          border: compact ? '2px solid var(--orange)' : '3px solid var(--orange)',
          padding: compact ? 14 : 18,
          marginBottom: compact ? 14 : 8,
          fontSize: compact ? 12 : 14,
          lineHeight: 1.5,
        }}
      >
        <b>{failure === 'outage' ? 'PŁATNOŚĆ CHWILOWO NIEDOSTĘPNA' : 'NIE UDAŁO SIĘ ROZPOCZĄĆ PŁATNOŚCI'}</b>
        <br />
        Twoje zamówienie <b>{order.id}</b> jest zapisane - nic nie przepadło. Spróbuj ponownie za
        chwilę, a jeśli to nie pomoże, napisz na{' '}
        <a href="mailto:kontakt@operacjateneryfa.pl" style={{ color: 'var(--orange)' }}>
          kontakt@operacjateneryfa.pl
        </a>
        .
        <div style={{ marginTop: 12 }}>
          <button className="btn full" onClick={() => setFailure(null)} disabled={loading}>
            SPRÓBUJ PONOWNIE
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      className="btn full"
      style={
        compact
          ? { marginBottom: 14 }
          : { marginBottom: 8, padding: '16px 22px', fontSize: 18 }
      }
      onClick={start}
      disabled={loading}
    >
      {loading ? 'PRZEKIEROWUJĘ…' : 'ZAPŁAĆ TERAZ (BLIK, KARTA, APPLE / GOOGLE PAY) →'}
    </button>
  );
}
